import React, { createContext, useContext, useState, useEffect } from 'react';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = () => {
      try {
        const token = localStorage.getItem('adminTokenNazai');
        const userData = localStorage.getItem('adminUserNazai');
        
        if (token && userData) {
          const parsedUser = JSON.parse(userData);
          
          // Check if user is admin or super_admin
          if (parsedUser.role === 'admin' || parsedUser.role === 'super_admin') {
            setUser(parsedUser);
            setIsAuthenticated(true);
          } else {
            // Clear invalid user data
            localStorage.removeItem('adminTokenNazai');
            localStorage.removeItem('adminUserNazai');
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        localStorage.removeItem('adminTokenNazai');
        localStorage.removeItem('adminUserNazai');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await adminAPI.login(credentials);
      
      // Check if user is admin or super_admin
      if (response.user.role !== 'admin' && response.user.role !== 'super_admin') {
        throw new Error('Accès refusé. Seuls les administrateurs peuvent se connecter.');
      }
      
      // Store token and user info
      localStorage.setItem('adminTokenNazai', response.access);
      if (response.refresh) {
        localStorage.setItem('adminRefreshTokenNazai', response.refresh);
      }
      localStorage.setItem('adminUserNazai', JSON.stringify(response.user));

      setUser(response.user);
      setIsAuthenticated(true);

      console.log('Login successful, user authenticated:', response.user);

      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await adminAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      clearAuthState();
    }
  };

  const clearAuthState = () => {
    localStorage.removeItem('adminTokenNazai');
    localStorage.removeItem('adminRefreshTokenNazai');
    localStorage.removeItem('adminUserNazai');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Function to handle automatic logout on auth errors
  const handleAuthError = () => {
    clearAuthState();
    toast.error('Session expirée. Veuillez vous reconnecter.');
    // The API utility will handle the redirect
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    handleAuthError,
    clearAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
